import React from 'react';
import ProjectCard from './ProjectCard';
import FilterModal from './FilterModal';

const ProjectsSection = ({ 
  skills, 
  filteredProjects, 
  sortOrder, 
  selectedTags, 
  searchQuery, 
  showFilterModal, 
  setSortOrder, 
  setSelectedTags, 
  setSearchQuery, 
  setShowFilterModal 
}) => {
  const handleApplyFilters = () => {
    // This function can be used for any additional logic when filters are applied
    // For now, it's just a placeholder
  };

  const handleRemoveTag = (tagToRemove) => {
    setSelectedTags(prev => prev.filter(tag => tag !== tagToRemove));
  };

  const getSortDisplayText = () => {
    switch (sortOrder) {
      case 'featured': return 'Featured';
      case 'newest': return 'Newest';
      case 'oldest': return 'Oldest';
      default: return 'Featured';
    }
  };

  return (
    <>
      <FilterModal
        isOpen={showFilterModal}
        onClose={() => setShowFilterModal(false)}
        skills={skills}
        selectedTags={selectedTags}
        onTagsChange={setSelectedTags}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        onApplyFilters={handleApplyFilters}
      />
      
      <section id="projects" className="py-20 bg-black">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-white mb-4">MY PROJECTS</h2>
            <div className="w-20 h-1 bg-blue-400 mx-auto mb-6" />
          </div>
          
          <div className="flex flex-col gap-4 mb-6 sm:grid sm:grid-cols-[1fr_auto_1fr] sm:items-center">
            {/* Showing count: on mobile this appears first; on sm+ it becomes the center column */}
            <div className="order-1 sm:order-2 flex justify-center">
              <span className="text-gray-400 text-sm">
                Showing {filteredProjects.length} projects
              </span>
            </div>

            {/* Wrapper for Filter + Sort: on mobile this is a single row (filter left, sort right);
                on sm+ the wrapper becomes `contents` so its children occupy the grid columns
                (filter -> left, sort -> right) while `Showing` sits in the middle */}
            <div className="flex items-center justify-between order-2 sm:contents">
              {/* Left side - Filter button and tags */}
              <div className="flex items-center gap-3 justify-start min-w-0 overflow-hidden order-1 sm:order-1">
                <button 
                  onClick={() => setShowFilterModal(true)} 
                  className="px-3 py-2 sm:px-4 bg-gray-700 text-gray-200 rounded-lg hover:bg-gray-600 transition-colors duration-200 flex items-center gap-2 flex-shrink-0"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                  </svg>
                  <span className="hidden xs:inline sm:inline">Filter</span>
                </button>
                
                <div className="hidden sm:flex items-center gap-2 min-w-0 flex-1">
                  {/* Search indicator - highest priority */}
                  {searchQuery.trim() && (
                    <div className="px-4 py-2 bg-blue-400 text-black rounded-lg hover:bg-blue-300 transition-colors duration-200 flex items-center gap-2 flex-shrink-0">
                      <span className="truncate">Search: "{searchQuery.length > 12 ? searchQuery.slice(0, 12) + '...' : searchQuery}"</span>
                      <button
                        onClick={() => setSearchQuery('')}
                        className="text-black hover:text-gray-800 transition-colors flex-shrink-0"
                      >
                        ×
                      </button>
                    </div>
                  )}
                  
                  {/* Display tags - limit based on available space */}
                  {(() => {
                    const maxTags = searchQuery.trim() ? 2 : 3; // Show fewer tags if search is present
                    const tagsToShow = selectedTags.slice(0, maxTags);
                    const remainingTags = selectedTags.length - maxTags;
                    
                    return (
                      <>
                        {tagsToShow.map(tag => (
                          <div
                            key={tag}
                            className="px-3 py-2 bg-blue-400 text-black rounded-lg hover:bg-blue-300 transition-colors duration-200 flex items-center gap-2 flex-shrink-0 max-w-24"
                          >
                            <span className="truncate text-sm">{tag}</span>
                            <button
                              onClick={() => handleRemoveTag(tag)}
                              className="text-black hover:text-gray-800 transition-colors flex-shrink-0"
                            >
                              ×
                            </button>
                          </div>
                        ))}
                        
                        {/* Show +N more if there are remaining tags */}
                        {remainingTags > 0 && (
                          <span className="text-gray-400 text-sm whitespace-nowrap flex-shrink-0">
                            +{remainingTags} more
                          </span>
                        )}
                      </>
                    );
                  })()}
                </div>
              </div>

              {/* Right side - Sort controls */}
              <div className="flex items-center justify-end gap-2 order-3 sm:order-3">
                <div className="flex items-center bg-gray-700 rounded-lg overflow-hidden">
                  {['featured', 'newest', 'oldest'].map((option) => (
                    <button
                      key={option}
                      onClick={() => setSortOrder(option)}
                      className={`px-2 py-2 sm:px-4 text-xs sm:text-sm transition-colors duration-200 ${
                        sortOrder === option
                          ? 'bg-blue-400 text-black'
                          : 'text-gray-300 hover:bg-gray-600 hover:text-white'
                      }`}
                    >
                      {option.charAt(0).toUpperCase() + option.slice(1)}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredProjects.length > 0 ? (
              filteredProjects.map(project => <ProjectCard key={project.id} project={project} />)
            ) : (
              <div className="col-span-full text-center py-12">
                <div className="text-gray-400 text-xl mb-4">No projects found</div>
                <div className="text-gray-500">
                  Try adjusting your search criteria or clearing the filters
                </div>
              </div>
            )}
          </div>
        </div>
      </section>
    </>
  );
};

export default ProjectsSection;
